import Station from '@/models/Station'
import Stations from '@/models/Stations'
import { DefinitionInfo } from '@/models/initData/DefinitionInfo'
import { LayoutInfo } from '@/models/initData/LayoutInfo'
import { IconInfo } from '@/models/initData/IconInfo'
import { CsvReport, CsvReports } from '@/models/initData/CsvReports'
import { NotificationInfo } from '@/models/initData/NotificationInfo'

export type MapInfo = {
  opacity?: number
  copyright?: {
    image: string
    link: string
    title: string
  }
  geojson: string
  mapSource: string
  position: {
    zoom: {
      max: number
      min: number
      default: number
    }
  } & Geometry
  rangeLimit: {
    leftUp: Geometry
    rightDown: Geometry
    minZoom: number
  }
}

export type SystemInfo = {
  analytics: string
  title?: string
  defaultPage: string
  logo: string
  favicon: string
  notification?: {
    id: string
  }
  mapInfo: MapInfo
}

export type ThresholdInfo = {
  colorInfo: {
    [type: string]: {
      color: string
    }
  }
}

export type Threshold = {
  comparison: number
  level: number
  id: string
  title: string
  type: string
  target: string
  status: string
}

export type StationGeometry = {
  address: string
  fieldId: string[]
  name: string
  stationId: string
  no?: number
  iconInfo?: IconInfo
  popupOffset?: {
    x: number
    y: number
  }
  offset?: {
    x: number
    y: number
  }
} & Geometry

export type CameraStation = {
  name: string
  postCode: number
  stationId: string
  link?: string
  pastCamera: boolean
} & StationGeometry

export type StationListItem = {
  stationId: string
  name: string
  areaCode: string
  threshold: Threshold[]
  ap?: number
  link?: string
  color?: string
  scale?: {
    maxSample: number
    min: number
    max: number
    span: number
  }
  waterThreshold?: number
} & StationGeometry

export type StationList = StationListItem[]

export type SummaryListStationTimeItem = {
  stationId: string
  fieldId: string[]
  alertProperty: (string | null)[]
  station?: Station
}

export type SummaryListStationTime = {
  title: string
  headerTime: string
  items: SummaryListStationTimeItem[]
}

export type SummaryListStation = {
  title: string
  hour?: SummaryListStationTime
  day?: SummaryListStationTime
  month?: SummaryListStationTime
}

export type SummaryList = {
  multiStation?: SummaryListStation[]
}

export type FieldListItem = {
  name: string
  unit: string
  decimal: number
  default: string
  type: string
  graphNames: {
    [type: string]: string
  }
  listNames: {
    [type: string]: string
  }
}

export type FieldList = {
  [name: string]: FieldListItem
}

export type Area = {
  id: string
  name: string
}

export type InitDataAttributes = {
  systemInfo: SystemInfo
  layoutInfo: LayoutInfo
  definitionInfo: DefinitionInfo
  stationList: StationList
  summaryList: SummaryList
  fieldList: FieldList
  cameraList: CameraStation[]
  areaList: Area[]
  iconInfo: IconInfo
  transactId: string
  csvReports: CsvReports
  thresholdInfo: ThresholdInfo
  notificationInfo?: NotificationInfo
}

export default class InitData {
  public systemInfo: SystemInfo
  public layoutInfo: LayoutInfo
  public definitionInfo: DefinitionInfo
  public stationList: StationList
  public cameraList: CameraStation[]
  public summaryList: SummaryList
  public fieldList: FieldList
  public stations: Stations
  public areaList: Area[]
  public iconInfo: IconInfo
  public transactId?: string
  public csvReports: CsvReports
  public thresholdInfo?: ThresholdInfo
  public notificationInfo?: NotificationInfo

  constructor(public attrs: InitDataAttributes) {
    this.systemInfo = attrs.systemInfo
    this.layoutInfo = attrs.layoutInfo
    this.definitionInfo = attrs.definitionInfo
    this.stationList = attrs.stationList
    this.summaryList = attrs.summaryList
    this.fieldList = attrs.fieldList
    this.stations = new Stations(this.stationList.map(stationAttributes => new Station(stationAttributes)))
    this.cameraList = attrs.cameraList
    this.fetchSummaryListStations()
    this.areaList = attrs.areaList
    this.iconInfo = attrs.iconInfo
    this.transactId = attrs.transactId
    this.csvReports = attrs.csvReports?.map(report => new CsvReport(report)) || []
    this.thresholdInfo = attrs.thresholdInfo
    this.notificationInfo = attrs.notificationInfo
  }

  public get areaCodes() {
    return this.areaList.map(a => a.id)
  }

  public get stationIds() {
    return this.stationList.map(s => s.stationId)
  }

  private fetchSummaryListStations() {
    if (!this.summaryList.multiStation) {
      return
    }

    const multiStation = this.summaryList.multiStation[0]

    Object.keys(multiStation).forEach(key => {
      if (key === 'title') {
        return
      }

      multiStation[key as 'hour' | 'day' | 'month']?.items.forEach(item => {
        item.station = this.stations.findById(item.stationId)
      })
    })
  }
}
